import {Component, computed, inject, signal, WritableSignal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AuthService} from '@services/auth.service';
import {NotificationService} from '@services/frontend/notification.service';

@Component({
  selector: 'app-reset-password',
  imports: [FormsModule, RouterLink],
  templateUrl: './reset-password.html'
})
export class ResetPassword {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);

  token = signal('');
  loading = signal(true);
  submitting = signal(false);
  tokenValid = signal(false);
  tokenChecked = signal(false);

  password: WritableSignal<string> = signal('');
  confirmPassword: WritableSignal<string> = signal('');
  error: WritableSignal<string> = signal('');

  private passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  isPasswordValid = computed(() => this.passwordRegex.test(this.password()));
  passwordsMatch = computed(() => this.password() === this.confirmPassword());
  canSubmit = computed(() =>
    this.tokenValid() && this.isPasswordValid() && this.passwordsMatch() && !this.submitting()
  );

  constructor() {
    const token = this.route.snapshot.queryParamMap.get('token') ?? '';
    this.token.set(token);

    if (!token) {
      this.tokenChecked.set(true);
      this.tokenValid.set(false);
      this.loading.set(false);
      return;
    }

    this.authService.validatePasswordResetToken(token).subscribe({
      next: (res) => {
        this.tokenChecked.set(true);
        this.tokenValid.set(res.valid);
        this.loading.set(false);
      },
      error: () => {
        this.tokenChecked.set(true);
        this.tokenValid.set(false);
        this.loading.set(false);
      }
    });
  }

  submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.submitting.set(true);
    this.error.set('');
    this.authService.resetPassword({
      token: this.token(),
      newPassword: this.password()
    }).subscribe({
      next: () => {
        this.submitting.set(false);
        this.notificationService.success('Mot de passe réinitialisé. Vous pouvez vous connecter.', 3500);
        void this.router.navigate(['/login']);
      },
      error: (err) => {
        this.submitting.set(false);
        const message = err?.error?.message || err?.error || 'Lien invalide ou expiré.';
        this.error.set(String(message));
      }
    });
  }
}
